import { action, autorun, computed, decorate, extendObservable } from "mobx";

class TemporarySectionStore {
  constructor(authStore, commonStore, compTixApi, routerStore, eventDetailsStore) {
    this.authStore = authStore;
    this.commonStore = commonStore;
    this.compTixApi = compTixApi;
    this.routerStore = routerStore;
    this.eventDetailsStore = eventDetailsStore;

    this.defaults = {
      homeTeamId: -1,
      awayTeamId: -1,
      departments: [],
      familyDepartments: [],
      friendsDepartments: [],
      selectedStatsId: -1,
      isHome: true,
      event: null,
      gamePk: -1,
      showAddTemporarySectionModal: false,
      departmentOptions: [],
      isSaving: false,
      sectionName: "",
      price: null,
      numOfSeats: null,
      sectionType: {},
      umpireCategoryType: null
    };

    extendObservable(this, {
      sectionName: this.defaults["sectionName"],
      departments: this.defaults["departments"],
      homeTeamId: this.defaults["homeTeamId"],
      awayTeamId: this.defaults["awayTeamId"],
      selectedStatsId: this.defaults["selectedStatsId"],
      sectionType: this.defaults["sectionType"],
      umpireCategoryType: this.defaults["umpireCategoryType"],
      price: this.defaults["price"],
      isHome: this.defaults["isHome"],
      numOfSeats: this.defaults["numOfSeats"],
      familyDepartments: this.defaults["familyDepartments"],
      friendsDepartments: this.defaults["friendsDepartments"],
      departmentOptions: this.defaults["departmentOptions"],
      event: this.defaults["event"],
      gamePk: this.defaults["gamePk"],
      venueSeasonId: this.defaults["venueSeasonId"],
      showAddTemporarySectionModal: this.defaults["showAddTemporarySectionModal"],
      isSaving: this.defaults["isSaving"],

      setSelectedStatsId: action(value => {
        this.selectedStatsId = value;
      }),
      setIsHome: action(value => {
        this.isHome = value;
      }),

      setHomeTeamId: action(value => {
        this.homeTeamId = value;
      }),

      setAwayTeamId: action(value => {
        this.awayTeamId = value;
      }),

      setSectionName: action(value => {
        this.sectionName = value;
      }),

      setPrice: action(value => {
        this.price = value;
      }),
      setNumOfSeats: action(value => {
        this.numOfSeats = value;
      }),
      setSectionType: action(value => {
        this.sectionType = value;
      }),
      setUmpireCategoryType: action(value => {
        this.umpireCategoryType = value;
      }),
      setDepartments: action(value => {
        this.departments = value;
      }),
      setEvent: action(value => {
        this.event = value;
      }),
      setSelectedFamilyDepartments: action(value => {
        this.familyDepartments = value;
      }),
      setSelectedFriendsDepartments: action(value => {
        this.friendsDepartments = value;
      }),
      setShowAddTemporarySectionModal: action(show => {
        this.showAddTemporarySectionModal = show;
        if (!show) {
          this.resetStore();
        }
      }),
      resetStore: action(() => {
        this.sectionName = this.defaults["sectionName"];
        this.sectionType = this.defaults["sectionType"];
        this.numOfSeats = this.defaults["numOfSeats"];
        this.price = this.defaults["price"];
        this.familyDepartments = this.defaults["familyDepartments"];
        this.friendsDepartments = this.defaults["friendsDepartments"];
        this.event = this.defaults["event"];
      }),
      saveSection: action(() => {
        const defaultCategoryType = commonStore.categoryTypes[0];
        const umpireCategorySection = this.commonStore.categoryTypes.filter(
          c => c.sectionCategoryId === this.umpireCategoryType?.value
        );
        const familyDepartmentMapping = this.familyDepartments.map(department => {
          return {
            departmentSectionId: null,
            label: department.department.departmentName,
            value: department.department.departmentTypeId,
            department: department.department.departmentType,
            departmentTypeId: department.department.departmentType.departmentTypeId,
            departmentName: department.department.departmentName,
            relation: "family",
            isDeleted: false
          };
        });
        //

        const friendsDepartments = this.friendsDepartments.map(department => {
          return {
            departmentSectionId: null,
            label: department.department.departmentName,
            value: department.department.departmentTypeId,
            department: department.department.departmentType,
            departmentTypeId: department.department.departmentType.departmentTypeId,
            departmentName: department.department.departmentName,
            relation: "friends",
            isDeleted: false
          };
        });

        const section = {
          gamePk: this.event.gamePk,
          sectionNumber: this.sectionName,
          sectionPrice: this.price,
          inventoryQuantity: this.numOfSeats,
          homeAwayType: this.sectionType.value,
          sectionCategory: this.umpireCategoryType?.value ? umpireCategorySection[0] : defaultCategoryType,
          isDeleted: false,
          departments: familyDepartmentMapping.concat(friendsDepartments)
        };
        const venueSeasonId = this.event.venueSeasonId;
        const gamePk = this.event.gamePk;
        compTixApi.saveTemporarySection(section, venueSeasonId).then(response => {
          this.setShowAddTemporarySectionModal(false);
          this.compTixApi.getSections(venueSeasonId).then(values => {
            this.compTixApi.getTemporarySections(gamePk).then(tempSections => {
              this.eventDetailsStore.setSections(values.concat(tempSections));
            });
          });
        });
      })
    });

    autorun(() => {
      if (this.event) {
        let orgId = 31; // default to umpire
        if (this.selectedStatsId != null) {
          if (this.selectedStatsId === this.event.teams.home.team.id) {
            orgId = this.homeTeamId;
          } else {
            orgId = this.awayTeamId;
          }
        }
        this.compTixApi.getDepartmentsByOrg(orgId).then(data => {
          this.setDepartments(data);
        });
      }
    });
  }

  get departmentDropdown() {
    return this.departments
      ? this.departments.map(d => ({
          label: d.departmentType.departmentName,
          value: d.departmentId,
          key: d.departmentId,
          department: d
        }))
      : [];
  }

  get isValidSave() {
    return (
      this.sectionName &&
      this.sectionType != null &&
      (this.familyDepartments.length > 0 || this.friendsDepartments.length > 0) &&
      this.price &&
      this.price >= 0 &&
      this.numOfSeats &&
      this.numOfSeats > 0
    );
  }
}

decorate(TemporarySectionStore, {
  departmentDropdown: computed,
  isValidSave: computed
});

export default TemporarySectionStore;
